import type { FC } from 'react';
import { useState } from 'react';

import { type UploadFile } from '@procurify/ui'

import { ImportAPI, type ImporterPayload } from '@webapp/api/ImportAPI'
import { type ImportType } from '@webapp/models/enums'
import { type Enum } from '@webapp/utils'
import {
  BaseImporter,
  BASE_IMPORTER_STAGES,
  type BaseImporterStage,
  type IBaseImporterProps,
} from './BaseImporter'
import { ImporterErrorStage } from './components/ImporterErrorStage/ImporterErrorStage'
import { ImporterProcessingStage } from './components/ImporterProcessingStage/ImporterProcessingStage'
import { type IImporterModifiedRowCounts } from './LegacyImporter'
import {
  type ImporterColumnMap,
  type ImporterErrorMsg,
  type ImporterSampleData,
} from './types'

export const IMPORTER_STAGES = {
  PROCESSING: 4,
  ERROR: -1,
  IMPROVED_ERROR: -2,
} as const
export type ImporterStage = Enum<typeof IMPORTER_STAGES>

const STAGES = { ...BASE_IMPORTER_STAGES, ...IMPORTER_STAGES }

const DEFAULT_COUNTS = {
  CREATE: 0,
  DEACTIVATE: 0,
  IGNORE: 0,
  UPDATE: 0,
}

export interface IImporterProps
  extends Pick<
    IBaseImporterProps<ImporterStage>,
    | 'title'
    | 'requiredFields'
    | 'optionalFields'
    | 'customfields'
    | 'forceOptionalCustomfields'
    | 'fieldNameMapper'
    | 'fieldMatcher'
    | 'criteria'
    | 'templateEndpoint'
    | 'importEndpoint'
    | 'onDismiss'
    | 'showUploadAssets'
  > {
  type?: ImportType
  errorMsg?: ImporterErrorMsg
}

export const Importer: FC<IImporterProps> = ({
  type,
  title,
  requiredFields,
  optionalFields,
  customfields,
  forceOptionalCustomfields,
  fieldNameMapper,
  fieldMatcher,
  criteria,
  templateEndpoint,
  showUploadAssets,
  importEndpoint,
  onDismiss,
}) => {
  const [stage, setStage] = useState<ImporterStage | BaseImporterStage>(
    STAGES.UPLOAD
  )
  const [uploadedCSV, setUploadedCSV] = useState<string>('')
  const [fallbackUploadedCSV, setFallbackUploadedCSV] =
    useState<string>('')
  const [sampleData, setSampleData] = useState<ImporterSampleData>(null)
  const [uploadedAssets, setUploadedAssets] = useState<string>('')
  const [numColumnsInCSV, setNumColumnsInCSV] = useState<number>(
    requiredFields.length
  )
  const [modifiedRowCounts, setModifiedRowCounts] =
    useState<IImporterModifiedRowCounts>(DEFAULT_COUNTS)
  const [errorCSVUrl, setErrorCSVUrl] = useState<string>('')
  const [rowsErrored, setRowsErrored] = useState<number>(0)

  const goToStage = (newStage: ImporterStage | BaseImporterStage) => {
    setStage(newStage)
  }

  const onCSVUpload = (file: UploadFile) => {
    const attachment = file?.rawResponse?.data

    setUploadedCSV(attachment?.uuid)
  }

  const onCSVRemove = () => {
    setUploadedCSV('')
    setFallbackUploadedCSV('')
  }

  const onSubmitCSVUpload = () => {
    goToStage(STAGES.UPLOADING)

    ImportAPI.getImportPreviewSample(type, uploadedCSV)
      .then((res) => res.data)
      .then((sampleDataResponse) => {
        const csvNumCols = sampleDataResponse?.number_of_columns || 0
        setNumColumnsInCSV(csvNumCols)

        if (csvNumCols < requiredFields.length) {
          goToStage(STAGES.UPLOAD)
        } else {
          setSampleData(sampleDataResponse)
          goToStage(STAGES.PREVIEW)
        }
      })
  }

  const onAssetUpload = (file: UploadFile) => {
    const attachment = file?.rawResponse?.data

    setUploadedAssets(attachment?.uuid)
  }

  const onAssetRemove = () => {
    setUploadedAssets('')
  }

  const onPreviewConfirm = ({
    mappedColumns,
    ignoreFirstRow,
  }: {
    mappedColumns: ImporterColumnMap
    ignoreFirstRow: boolean
  }): void => {
    const csvPayload: ImporterPayload = {
      column_mapping: mappedColumns,
      csv_uuid: uploadedCSV || fallbackUploadedCSV,
      ignore_first_row: ignoreFirstRow,
    }

    if (uploadedAssets) {
      csvPayload.images_zipfile = uploadedAssets
    }

    ImportAPI.updateCSVImportFormat(type, uploadedCSV, csvPayload).then(() => {
      goToStage(STAGES.PROCESSING)
    })
  }

  const onPreviewCancel = () => {
    goToStage(STAGES.UPLOAD)
  }

  const onImportSuccess = (importResponse) => {
    setModifiedRowCounts({
      CREATE: importResponse.created,
      DEACTIVATE: 0,
      IGNORE: 0,
      UPDATE: importResponse.updated,
    })
    goToStage(STAGES.COMPLETE)
  }

  const onImportError = (csvURL, numRowsErrored) => {
    if (uploadedCSV) setFallbackUploadedCSV(uploadedCSV)
    setUploadedCSV('')
    setErrorCSVUrl(csvURL)
    setRowsErrored(numRowsErrored)
    goToStage(STAGES.ERROR)
  }

  return (
    <BaseImporter<ImporterStage>
      title={title}
      requiredFields={requiredFields}
      optionalFields={optionalFields}
      customfields={customfields}
      forceOptionalCustomfields={forceOptionalCustomfields}
      fieldNameMapper={fieldNameMapper}
      fieldMatcher={fieldMatcher}
      criteria={criteria}
      templateEndpoint={templateEndpoint}
      importEndpoint={importEndpoint}
      showUploadAssets={showUploadAssets}
      stage={stage}
      sampleData={sampleData}
      modifiedRowCounts={modifiedRowCounts}
      noFileUploaded={!uploadedCSV}
      customStages={{
        [STAGES.PROCESSING]: (
          <ImporterProcessingStage
            csvUUID={uploadedCSV}
            onSuccess={onImportSuccess}
            onError={onImportError}
            type={type}
            key={STAGES.PROCESSING}
          />
        ),
        [STAGES.ERROR]: (
          <ImporterErrorStage
            title={title}
            onDismiss={onDismiss}
            csvURL={errorCSVUrl}
            failedRowCount={rowsErrored}
            onCSVUpload={onCSVUpload}
            onCSVRemove={onCSVRemove}
            onSubmitCSVUpload={onSubmitCSVUpload}
            onAssetUpload={onAssetUpload}
            onAssetRemove={onAssetRemove}
            disableSubmitButton={!uploadedCSV}
            key={STAGES.ERROR}
          />
        ),
      }}
      numColumnsInCSV={numColumnsInCSV}
      onCSVUpload={onCSVUpload}
      onCSVRemove={onCSVRemove}
      onAssetUpload={onAssetUpload}
      onAssetRemove={onAssetRemove}
      onSubmitCSVUpload={onSubmitCSVUpload}
      onPreviewConfirm={onPreviewConfirm}
      onPreviewCancel={onPreviewCancel}
      onDismiss={onDismiss}
    />
  )
}
